import React, { useMemo, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CollapsibleTable from '../../components/table';
import ExportButton from '../../components/BotonEx';
import ModalNivelAut from '../../components/ModalNivelAut';
import ModalSincronizarERP from '../../components/ModalSincronizarERP';
import ModalCentroCosto from '../../components/ModalCentroCosto';
import ModalUsuarios from '../../components/ModalUsuarios';
import ModalDepartamento from '../../components/ModalDepartamento';
import SnackbarAlert from '../../components/SnackbarAlert';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import config from '../../config/config';
import BotonAgregar from '../../components/BotonAgregar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterComponent from '../../components/FilterComponent';
import { useNivelAutController } from './hooks/useNivelAutController';
import { Box, Paper, TextField, Autocomplete, Button, Typography, CircularProgress, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SyncIcon from '@mui/icons-material/Sync';

// Helpers para filtros multiselect
const splitFilterValue = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return String(val).split(',').map(s => s.trim()).filter(Boolean);
};

const compactTextFieldSx = {
  '& .MuiInputBase-root': { height: 34, fontSize: 13 },
  '& .MuiInputLabel-root': { fontSize: 13, top: -4 },
  '& .MuiInputLabel-shrink': { top: 0 }
};

export default function NivelAut() {
  const apiUrl = config.apiBaseUrl;

  const {
    // Datos
    data,
    loadingData,
    loadingAction,
    usuarioId,
    empresaIdPref,

    // Datos auxiliares para modales
    dataEmpresa,
    dataDivisas,
    dataSucursal,
    dataCentrocosto,
    dataDepartamento,
    dataUsuario,
    dataUsuarioAutorizadores,
    dataUsuarioTesoreros,
    dataRolesUsuario,

    // Estado modales
    modalOpen,
    setModalOpen,
    modalCentroCostoOpen,
    setModalCentroCostoOpen,
    modalUsuarioOpen,
    setModalUsuarioOpen,
    modalDepartamentoOpen,
    setModalDepartamentoOpen,
    confirmModal,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,

    // Formularios
    formData,
    setFormData,
    formCentroCosto,
    setFormCentroCosto,
    formDepartamento,
    setFormDepartamento,
    nuevoUsuario,
    setNuevoUsuario,
    excelData,
    setExcelData,
    filterData,
    setFilterData,
    loggedInUsername,
    empresaIds,

    // Acciones
    fetchData,
    handleExportFetchAll,
    handleTableStateChange,
    handleOpenModal,
    handleCloseModal,
    handleInputChange,
    crearEditarRegistro,
    confirmarCambiarEstado,
    handleConfirmAction,
    handleCancelAction,
    handleGuardarNuevoUsuario,
    handleImportExcelNivelAut,
    handleSincronizarErp,
    handlePreviewSincronizar,
    loadingSync,
    loadingPreview,
    previewData,
    modalSincronizarOpen,
    setModalSincronizarOpen,
    resetModalStates,

    // Setters de dependencias
    setDataCentrocosto,
    setDataDepartamento,
    filteredData,
  } = useNivelAutController();

  // ─── Estados de filtros en encabezado ──────────────────────────────────
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [ccFilter, setCcFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [estatusFilter, setEstatusFilter] = useState('');
  const [quickSearch, setQuickSearch] = useState('');

  // ─── Botones de Acción ───────────────────────────────────────────────
  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Editar Registro',
      onClick: (row) => handleOpenModal(row, false),
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 2,
      label: 'Copiar Registro',
      onClick: (row) => handleOpenModal(row, true),
      icon: <ContentCopyIcon />,
      buttonColor: '#455a64',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !Number(row.ACTIVO),
    },
    {
      id: 3,
      label: (row) => (Number(row.ACTIVO) ? 'Desactivar' : 'Activar'),
      onClick: (row) => confirmarCambiarEstado(row),
      icon: (row) => Number(row.ACTIVO) ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => Number(row.ACTIVO) ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: false,
    },
  ], [handleOpenModal, confirmarCambiarEstado]);

  // ─── Opciones Dinámicas de Filtros ───────────────────────────────────
  const rawRows = useMemo(() => Array.isArray(data?.contenido) ? data.contenido : [], [data]);

  const empresaOptions = useMemo(() => {
    const set = new Set();
    rawRows.forEach(row => {
      const val = row.Empresa || row.NOMBRE_EMPRESA || '';
      if (val && val.trim()) set.add(val.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rawRows]);

  const ccOptions = useMemo(() => {
    const set = new Set();
    rawRows.forEach(row => {
      const val = row.CentroCosto || row.CENTRO_COSTO || '';
      if (val && val.trim()) set.add(val.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rawRows]);

  const deptOptions = useMemo(() => {
    const set = new Set();
    rawRows.forEach(row => {
      const val = row.Departamento || row.NOMBRE_DEPARTAMENTO || '';
      if (val && val.trim()) set.add(val.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rawRows]);

  // ─── Mapeo y Filtrado de Datos para la Tabla ──────────────────────────
  const dataForTable = useMemo(() => {
    let rows = [...rawRows];

    const selectedEmpresas = splitFilterValue(empresaFilter);
    if (selectedEmpresas.length > 0) {
      rows = rows.filter(row => {
        const val = row.Empresa || row.NOMBRE_EMPRESA || '';
        return selectedEmpresas.some(s => val.toLowerCase().includes(s.toLowerCase()));
      });
    }

    const selectedCcs = splitFilterValue(ccFilter);
    if (selectedCcs.length > 0) {
      rows = rows.filter(row => {
        const val = row.CentroCosto || row.CENTRO_COSTO || '';
        return selectedCcs.some(s => val.toLowerCase().includes(s.toLowerCase()));
      });
    }

    const selectedDepts = splitFilterValue(deptFilter);
    if (selectedDepts.length > 0) {
      rows = rows.filter(row => {
        const val = row.Departamento || row.NOMBRE_DEPARTAMENTO || '';
        return selectedDepts.some(s => val.toLowerCase().includes(s.toLowerCase()));
      });
    }

    if (estatusFilter) {
      if (estatusFilter === 'Activo') {
        rows = rows.filter(row => Number(row.ACTIVO) === 1);
      } else if (estatusFilter === 'Inactivo') {
        rows = rows.filter(row => Number(row.ACTIVO) === 0);
      }
    }

    if (quickSearch && quickSearch.trim()) {
      const q = quickSearch.toLowerCase().trim();
      rows = rows.filter(row => {
        return Object.values(row || {}).some(val => String(val || '').toLowerCase().includes(q));
      });
    }

    const mappedRows = rows.map(row => {
      const isActivo = Number(row.ACTIVO) === 1;
      const userName = row.Usuario || row.USER_NAME || 'N/D';
      const usuarioIdVal = row.ID || 'N/D';

      // Serializar autorizadores y tesoreros en el valor de la columna de forma directa
      const autNames = [];
      const tesNames = [];
      for (let i = 1; i <= 10; i++) {
        const val = row[`AUTORIZADOR ${i}`] || row[`AUTORIZADOR${i}`];
        if (val && val !== 'N/A' && val !== 'N/D' && val !== '-') {
          autNames.push({ name: String(val).trim(), level: i });
        }
      }
      for (let i = 1; i <= 5; i++) {
        const val = row[`TESORERO ${i}`] || row[`TESORERO${i}`];
        if (val && val !== 'N/A' && val !== 'N/D' && val !== '-') {
          tesNames.push({ name: String(val).trim(), level: i });
        }
      }

      const compactRow = {
        USUARIO_USUARIO_ID: userName,
        EMPRESA_DIVISA: row.Empresa || row.NOMBRE_EMPRESA || 'N/D',
        CENTRO_DEPTO: row.CentroCosto || row.CENTRO_COSTO || 'N/D',
        AUTORIZADORES: JSON.stringify(autNames),
        TESOREROS: JSON.stringify(tesNames),
        ESTATUS_COMPACTO: isActivo ? 'Activo' : 'Inactivo',
        ACTIVO: Number(row.ACTIVO),
        USER_NAME: userName,
        USUARIOID: usuarioIdVal,
        ID: row.ID,
      };

      Object.keys(row || {}).forEach((key) => {
        if (key === 'partidas') return;
        compactRow[key] = row[key];
      });

      return compactRow;
    });

    // Inactivos al final manteniendo el orden de activos
    mappedRows.sort((a, b) => {
      const aAct = Number(a.ACTIVO) === 1 ? 1 : 0;
      const bAct = Number(b.ACTIVO) === 1 ? 1 : 0;
      return bAct - aAct;
    });

    return mappedRows;
  }, [rawRows, empresaFilter, ccFilter, deptFilter, estatusFilter, quickSearch]);

  const handleResetFilters = () => {
    setEmpresaFilter('');
    setCcFilter('');
    setDeptFilter('');
    setEstatusFilter('');
    setQuickSearch('');
  };

  // ─── Columnas agrupadas: un solo key por grupo ────────────────────────────────
  const initialColumnOrder = useMemo(() => [
    'USUARIO_USUARIO_ID',
    'EMPRESA_DIVISA',
    'CENTRO_DEPTO',
    'AUTORIZADORES',
    'TESOREROS',
    'ESTATUS_COMPACTO',
  ], []);

  const columnLabels = useMemo(() => ({
    USUARIO_USUARIO_ID: 'Usuario / ID',
    EMPRESA_DIVISA: 'Empresa / Divisa',
    CENTRO_DEPTO: 'Centro de Costo / Depto',
    AUTORIZADORES: 'Autorizadores',
    TESOREROS: 'Tesorero',
    ESTATUS_COMPACTO: 'Estatus',
  }), []);

  // Ocultar las columnas individuales dinámicas AUTORIZADOR1, TESORERO1, etc.
  const dynamicHiddenFields = useMemo(() => {
    const keys = [];
    for (let i = 1; i <= 10; i++) {
      keys.push(`AUTORIZADOR ${i}`, `AUTORIZADOR${i}`);
    }
    for (let i = 1; i <= 5; i++) {
      keys.push(`TESORERO ${i}`, `TESORERO${i}`);
    }
    return keys;
  }, []);

  const hiddenFieldsMemo = useMemo(() => [
    'FECHACREACION',
    'ACTIVO',
    'Sucursal',
    'ID',
    'USER_NAME',
    'USUARIOID',
    'Usuario',
    'Empresa',
    'CentroCosto',
    'Departamento',
    'Divisa',
    ...dynamicHiddenFields,
  ], [dynamicHiddenFields]);

  const nivelAutColumns = useMemo(() => {
    if (!data?.contenido || data.contenido.length === 0) return null;

    const baseColumns = [
      { key: 'ID',           label: 'ID' },
      { key: 'Usuario',      label: 'USUARIO' },
      { key: 'Empresa',      label: 'EMPRESA' },
      { key: 'CentroCosto',  label: 'CENTRO DE COSTO' },
      { key: 'Departamento', label: 'DEPARTAMENTO' },
      { key: 'Divisa',       label: 'DIVISA' },
    ];

    const dynamicKeys = new Set();
    data.contenido.forEach(row => {
      Object.keys(row).forEach(k => {
        if (k.startsWith('AUTORIZADOR') || k.startsWith('TESORERO')) {
          dynamicKeys.add(k);
        }
      });
    });

    const sortedDynamic = Array.from(dynamicKeys).sort((a, b) => {
      const isAutA = a.startsWith('AUTORIZADOR');
      const isAutB = b.startsWith('AUTORIZADOR');
      if (isAutA && !isAutB) return -1;
      if (!isAutA && isAutB) return 1;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });

    const mappedDynamic = sortedDynamic.map(k => ({
      key: k,
      label: k.toUpperCase()
    }));

    return [
      ...baseColumns,
      ...mappedDynamic,
      { key: 'ACTIVO',        label: 'ESTATUS' },
      { key: 'FECHACREACION',label: 'FECHA CREACIÓN' },
    ];
  }, [data]);

  const dataTableProp = useMemo(() => ({
    total: Number(data?.total || dataForTable.length),
    contenido: dataForTable
  }), [data?.total, dataForTable]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 'clamp(6px, 0.8vw, 12px)', gap: 'clamp(6px, 0.8vw, 10px)', bgcolor: '#f8fafc' }}>
      {/* Barra de Filtros con clamp */}
      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          px: 'clamp(8px, 1vw, 12px)',
          py: 'clamp(5px, 0.6vw, 8px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(4px, 0.5vw, 8px)',
          flexWrap: 'wrap',
          '& .MuiOutlinedInput-root': {
            height: 'clamp(26px, 2.2vw, 32px) !important',
            fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem) !important',
            borderRadius: '7px !important',
          },
          '& .MuiInputLabel-root': {
            fontSize: 'clamp(0.62rem, 0.58rem + 0.05vw, 0.70rem) !important',
            top: '-3px !important',
          }
        }}
      >
        <Autocomplete
          multiple
          forcePopupIcon
          filterSelectedOptions
          size="small"
          options={empresaOptions}
          value={splitFilterValue(empresaFilter)}
          onChange={(_, value) => setEmpresaFilter(value.join(','))}
          renderInput={(params) => <TextField {...params} label="Empresa" placeholder="Empresas..." sx={{ width: 'clamp(140px, 14vw, 190px)' }} />}
          sx={{ width: 'clamp(140px, 14vw, 190px)' }}
        />

        <Autocomplete
          multiple
          forcePopupIcon
          filterSelectedOptions
          size="small"
          options={ccOptions}
          value={splitFilterValue(ccFilter)}
          onChange={(_, value) => setCcFilter(value.join(','))}
          renderInput={(params) => <TextField {...params} label="Centro de Costo" placeholder="Centros..." sx={{ width: 'clamp(140px, 14vw, 190px)' }} />}
          sx={{ width: 'clamp(140px, 14vw, 190px)' }}
        />

        <Autocomplete
          multiple
          forcePopupIcon
          filterSelectedOptions
          size="small"
          options={deptOptions}
          value={splitFilterValue(deptFilter)}
          onChange={(_, value) => setDeptFilter(value.join(','))}
          renderInput={(params) => <TextField {...params} label="Departamento" placeholder="Deptos..." sx={{ width: 'clamp(140px, 14vw, 190px)' }} />}
          sx={{ width: 'clamp(140px, 14vw, 190px)' }}
        />

        <Autocomplete
          size="small"
          options={['Activo', 'Inactivo']}
          value={estatusFilter || null}
          onChange={(_, value) => setEstatusFilter(value || '')}
          renderInput={(params) => <TextField {...params} label="Estatus" placeholder="Todos" sx={{ width: 'clamp(95px, 9vw, 120px)' }} />}
          sx={{ width: 'clamp(95px, 9vw, 120px)' }}
        />

        <TextField
          size="small"
          label="Búsqueda rápida"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
          placeholder="Buscar..."
          sx={{ minWidth: 'clamp(140px, 16vw, 220px)', flexGrow: 1 }}
        />

        <Box sx={{ display: 'flex', gap: 0.6, ml: 'auto' }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<SearchIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2.2vw, 32px)',
              minWidth: 0,
              px: 'clamp(8px, 0.8vw, 14px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '7px',
              boxShadow: 'none',
            }}
          >
            Buscar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={handleResetFilters}
            startIcon={<RestartAltIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2.2vw, 32px)',
              minWidth: 0,
              px: 'clamp(8px, 0.8vw, 12px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '7px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' }
            }}
          >
            Limpiar
          </Button>
        </Box>
      </Paper>

      {/* Componente secundario de importación excel en segundo plano si se requiere */}
      <Box sx={{ display: 'none' }}>
        <FilterComponent
          importar
          importMode="excel"
          data={data}
          filterData={filterData}
          setFilterData={setFilterData}
          handleClick={() => {}}
          setExcelData={setExcelData}
          handleImport={handleImportExcelNivelAut}
          showHeaderActions={false}
          showFilterButton={false}
          showExportButton={false}
          persistEnabled
          persistKey="NivelAut"
          persistVista="filters"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
        />
      </Box>

      {/* Contenedor de Tabla con el mismo formato estándar */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          loading={loadingData}
          dataTable={dataTableProp}
          buttons={buttons}
          enableSorting={true}
          hiddenFields={hiddenFieldsMemo}
          enableFiltering={false}
          onEndReached={() => fetchData(true)}
          onStateChange={handleTableStateChange}
          density="small"
          compactActions
          maxInlineActions={3}
          initialColumnOrder={initialColumnOrder}
          columnLabels={columnLabels}
          leftButtons={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <BotonAgregar
                title="Agregar Nivel"
                onClick={() => {
                  resetModalStates();
                  setModalOpen(true);
                }}
              />
              <Tooltip title="Sincroniza automáticamente los niveles de autorización desde los datos del ERP, evitando duplicados." arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loadingSync}
                    onClick={() => setModalSincronizarOpen(true)}
                    startIcon={
                      loadingSync
                        ? <CircularProgress size={12} color="inherit" />
                        : <SyncIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />
                    }
                    sx={{
                      height: 'clamp(26px, 2.2vw, 32px)',
                      px: 'clamp(8px, 0.8vw, 14px)',
                      fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: '7px',
                      borderColor: '#0655A5',
                      color: '#0655A5',
                      '&:hover': { bgcolor: 'rgba(6,85,165,0.06)', borderColor: '#0655A5' },
                    }}
                  >
                    {loadingSync ? 'Sincronizando...' : 'Sincronizar ERP'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
          rightButtons={
            <ExportButton
              data={dataForTable}
              onFetchAll={handleExportFetchAll}
              fileName="NivelesAutorizacion.xlsx"
              columnMap={nivelAutColumns}
            />
          }
          persistEnabled
          persistKey="NivelAut_v5"
          persistVista="layout"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
        />
      </Box>

      <ModalNivelAut
        isOpen={modalOpen}
        handleClose={handleCloseModal}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSave={crearEditarRegistro}
        dataEmpresa={dataEmpresa}
        dataSucursal={dataSucursal}
        dataCentrocosto={dataCentrocosto}
        dataDepartamento={dataDepartamento}
        dataUsuario={dataUsuario}
        dataUsuarioAutorizadores={dataUsuarioAutorizadores}
        dataUsuarioTesoreros={dataUsuarioTesoreros}
        dataDivisas={dataDivisas}
      />

      <ModalUsuarios
        isOpen={modalUsuarioOpen}
        handleClose={() => setModalUsuarioOpen(false)}
        handleSave={handleGuardarNuevoUsuario}
        formData={nuevoUsuario}
        handleInputChange={(field, value) =>
          setNuevoUsuario((prev) => ({ ...prev, [field]: value }))
        }
        dataEmpresa={dataEmpresa}
        dataRool={dataRolesUsuario}
        editPasswordOnly={false}
      />

      <ModalDepartamento
        open={modalDepartamentoOpen}
        onClose={() => setModalDepartamentoOpen(false)}
        onSave={async () => {
          setModalDepartamentoOpen(false);
          const empresaId = Number(formData.Empresa);
          try {
            const { default: axios } = await import('axios');
            const departamentoRes = await axios.post(
              `${apiUrl}/nivelAutDepartamento`,
              { EmpresaId: empresaId }
            );
            setDataDepartamento(departamentoRes.data);

            const nuevo = departamentoRes.data.find(
              (d) => d.NOMBRE_DEPARTAMENTO === formDepartamento.NOMBRE_DEPARTAMENTO
            );
            if (nuevo) {
              setFormData((prev) => ({ ...prev, Departamento: nuevo.DEPARTAMENTOID }));
            }
          } catch (err) {
            console.error('Error actualizando departamentos:', err);
          }
        }}
        formData={formDepartamento}
        handleInputChange={(field, value) =>
          setFormDepartamento((prev) => ({ ...prev, [field]: value }))
        }
        usuario={loggedInUsername}
        empresaId={empresaIds}
      />

      <ModalCentroCosto
        open={modalCentroCostoOpen}
        onClose={() => setModalCentroCostoOpen(false)}
        onSave={async () => {
          setModalCentroCostoOpen(false);
          const empresaId = Number(formData.Empresa);
          try {
            const { default: axios } = await import('axios');
            const centroCostoRes = await axios.post(
              `${apiUrl}/nivelAutCentroCosto`,
              { EmpresaId: empresaId }
            );
            setDataCentrocosto(centroCostoRes.data);

            if (centroCostoRes.data.length > 0) {
              const ultimo = centroCostoRes.data.reduce(
                (max, c) => (c.CCID > max.CCID ? c : max),
                centroCostoRes.data[0]
              );
              setFormData((prev) => ({ ...prev, CentroCosto: ultimo.CCID }));
            }
          } catch (err) {
            console.error('Error actualizando centros de costo:', err);
          }
        }}
        centroCostoId={null}
        formData={formCentroCosto}
        handleInputChange={(field, value) =>
          setFormCentroCosto((prev) => ({ ...prev, [field]: value }))
        }
        usuario={loggedInUsername}
        empresaId={empresaIds}
      />

      <SnackbarAlert
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
      />

      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />

      <SimpleSuccessSpinner
        open={loadingAction}
        handleClose={() => {}}
        message="Procesando, por favor espere..."
      />

      <ModalSincronizarERP
        isOpen={modalSincronizarOpen}
        handleClose={() => setModalSincronizarOpen(false)}
        onPreview={handlePreviewSincronizar}
        onConfirmar={handleSincronizarErp}
        loadingPreview={loadingPreview}
        loadingSync={loadingSync}
        previewData={previewData}
        dataUsuarioAutorizadores={dataUsuarioAutorizadores}
        dataUsuarioTesoreros={dataUsuarioTesoreros}
      />
    </Box>
  );
}